import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../api/api';

const IntegrationManagement = () => {
  const [summary, setSummary] = useState({
    total_integrations: 3,
    active_integrations: 2,
    total_synced: 2190,
    success_rate: 96.0,
    recent_syncs_24h: 131
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [syncData, setSyncData] = useState({ records: [], stats: null });
  const [loadingSyncData, setLoadingSyncData] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleCreateIntegration = async (formData) => {
    try {
      setCreating(true);
      
      const integrationData = {
        name: formData.name,
        integration_type: formData.type,
        provider: formData.provider,
        config: formData.config || {},
        credentials: formData.credentials || {},
        endpoints: formData.endpoints || {}
      };

      const response = await fetch(getApiUrl('/api/integrations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(integrationData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Integration "${formData.name}" created successfully!`);
        setShowCreateModal(false);
        handleRefresh(); // Refresh the data
      } else {
        const error = await response.text();
        alert(`Failed to create integration: ${error}`);
      }
    } catch (error) {
      console.error('Error creating integration:', error);
      alert('Error creating integration. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleIntegrationClick = async (integrationId, integrationName) => {
    try {
      setLoadingSyncData(true);
      setSelectedIntegration({ id: integrationId, name: integrationName });
      
      // Fetch sync records and stats for this integration
      const [recordsRes, statsRes] = await Promise.all([
        fetch(getApiUrl(`/api/integrations/${integrationId}/sync/records?limit=20`)),
        fetch(getApiUrl(`/api/integrations/${integrationId}/sync/stats`))
      ]);

      let records = [];
      let stats = null;

      if (recordsRes.ok) {
        const recordsData = await recordsRes.json();
        records = recordsData.records || [];
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        stats = statsData.stats || null;
      }

      // If no specific data, get general sync data for demo
      if (records.length === 0) {
        const generalRes = await fetch(getApiUrl('/api/integrations/sync/records?limit=10'));
        if (generalRes.ok) {
          const generalData = await generalRes.json();
          records = (generalData.records || []).slice(0, 5); // Show sample data
        }
      }

      setSyncData({ records, stats });
    } catch (error) {
      console.error('Error fetching sync data:', error);
      // Set mock data for demonstration
      setSyncData({
        records: generateMockSyncRecords(integrationName),
        stats: generateMockStats(integrationId)
      });
    } finally {
      setLoadingSyncData(false);
    }
  };

  const generateMockSyncRecords = (integrationName) => [
    {
      sync_id: 'sync_001',
      direction: 'outgoing',
      data_type: 'incident',
      external_id: 'INC0000123',
      internal_id: 'INC-CLOUD-001',
      status: 'completed',
      sync_started_at: new Date(Date.now() - 300000).toISOString(),
      sync_completed_at: new Date(Date.now() - 290000).toISOString(),
      data_payload: {
        title: 'Database Connection Pool Exhaustion',
        description: 'Production database connection pool exhausted causing timeouts',
        severity: 'high',
        category: 'Database',
        agent_notes: 'HawkAI Agent Analysis: Detected connection pool saturation. Recommended increasing max_connections from 100 to 200.'
      },
      sync_notes: [`Successfully synced incident to ${integrationName}`, 'Agent recommendations included in notes']
    },
    {
      sync_id: 'sync_002',
      direction: 'outgoing', 
      data_type: 'incident_update',
      external_id: 'INC0000124',
      internal_id: 'INC-CLOUD-002',
      status: 'completed',
      sync_started_at: new Date(Date.now() - 180000).toISOString(),
      sync_completed_at: new Date(Date.now() - 170000).toISOString(),
      data_payload: {
        status: 'investigating',
        agent_notes: 'HawkAI Root Cause Analysis: Network latency spike detected at 14:30 UTC. Probable cause: BGP route flapping on ISP side. Recommended monitoring network paths and contacting ISP.',
        priority: 'high',
        assigned_to: 'network-team@company.com'
      },
      sync_notes: ['Agent analysis synced successfully', 'Root cause findings added to incident']
    },
    {
      sync_id: 'sync_003',
      direction: 'incoming',
      data_type: 'incident_update',
      external_id: 'INC0000125', 
      internal_id: '',
      status: 'completed',
      sync_started_at: new Date(Date.now() - 60000).toISOString(),
      sync_completed_at: new Date(Date.now() - 50000).toISOString(),
      data_payload: {
        number: 'INC0000125',
        short_description: 'API Response Time Degradation',
        state: 'In Progress',
        work_notes: [
          {
            created_at: new Date(Date.now() - 30000).toISOString(),
            author: 'hawkai.agent',
            note: 'HawkAI Predictive Analysis: API response time trending upward. Current avg: 2.3s (baseline: 0.8s). Recommending immediate cache optimization and database query review.'
          }
        ]
      },
      sync_notes: [`Received update from ${integrationName}`, 'HawkAI agent notes detected']
    }
  ];

  const generateMockStats = (integrationId) => ({
    integration_id: integrationId,
    total_synced: 1247,
    successful_syncs: 1201,
    failed_syncs: 46,
    success_rate: 96.3,
    last_24h_syncs: 89,
    avg_sync_time: 0.45,
    last_sync_at: new Date(Date.now() - 50000).toISOString()
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Integration Management</h1>
          <p className="text-gray-600">Monitor data synchronization and integration performance</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleRefresh} 
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Integration
          </button>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="rounded-xl border bg-white shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <div className="h-5 w-5 text-blue-600">📊</div>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.total_integrations || 0}</p>
              <p className="text-sm text-gray-600">Total Integrations</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <div className="h-5 w-5 text-green-600">⚡</div>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.active_integrations || 0}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <div className="h-5 w-5 text-purple-600">🔄</div>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.total_synced || 0}</p>
              <p className="text-sm text-gray-600">Total Syncs</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <div className="h-5 w-5 text-yellow-600">📈</div>
            </div>
            <div>
              <p className="text-2xl font-bold">{(summary.success_rate || 0).toFixed(1)}%</p>
              <p className="text-sm text-gray-600">Success Rate</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-white shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <div className="h-5 w-5 text-orange-600">⚡</div>
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.recent_syncs_24h || 0}</p>
              <p className="text-sm text-gray-600">Last 24h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Content */}
      <div className="space-y-6">
        <div className="rounded-xl border bg-white shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Integration Management Dashboard</h2>
          <p className="text-gray-600 mb-2">Enhanced IntegrationManagement.js component with sync data display functionality.</p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800">
              💡 <strong>Click on any active integration</strong> below to view raw sync data, including Agentic AI updates to incidents and notes.
            </p>
          </div>
          
          <div className="space-y-4">
            <div 
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-300 ${selectedIntegration?.id === 'servicenow_prod' ? 'bg-blue-50 border-blue-300' : ''}`}
              onClick={() => handleIntegrationClick('servicenow_prod', 'ServiceNow Integration')}
            >
              <h3 className="font-medium mb-2">🟦 ServiceNow Integration</h3>
              <p className="text-sm text-gray-600">Status: Active • Last Sync: 15m ago</p>
              <div className="mt-2 flex justify-between items-center">
                <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Live Sync</span>
                <span className="text-xs text-blue-600">Click to view sync data →</span>
              </div>
            </div>
            
            <div 
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-300 ${selectedIntegration?.id === 'jira_prod' ? 'bg-blue-50 border-blue-300' : ''}`}
              onClick={() => handleIntegrationClick('jira_prod', 'Jira Service Management')}
            >
              <h3 className="font-medium mb-2">🔷 Jira Service Management</h3>
              <p className="text-sm text-gray-600">Status: Active • Last Sync: 20m ago</p>
              <div className="mt-2 flex justify-between items-center">
                <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Live Sync</span>
                <span className="text-xs text-blue-600">Click to view sync data →</span>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 opacity-60">
              <h3 className="font-medium mb-2">💬 Slack Operations</h3>
              <p className="text-sm text-gray-600">Status: Inactive • Last Sync: 2h ago</p>
              <div className="mt-2">
                <span className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">Inactive</span>
              </div>
            </div>
          </div>

          {/* Raw Integration Data Section */}
          {selectedIntegration && (
            <div className="mt-6">
              <div className="bg-gray-900 text-green-400 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-green-300">
                    🔍 Integration Sync Data - {selectedIntegration.name}
                  </h4>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowRawData(!showRawData)}
                      className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-gray-300"
                    >
                      {showRawData ? '📋 List View' : '🔧 Raw JSON'}
                    </button>
                    <button 
                      onClick={() => setSelectedIntegration(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {loadingSyncData ? (
                  <div className="text-center py-8">
                    <div className="text-blue-400">⚡ Loading sync data...</div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Sync Statistics */}
                    {syncData.stats && (
                      <div className="bg-gray-800 rounded p-3">
                        <h5 className="text-yellow-400 font-medium mb-2">📊 Integration Statistics</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Total Synced:</span>
                            <span className="text-white ml-2">{syncData.stats.total_synced}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Success Rate:</span>
                            <span className="text-green-400 ml-2">{syncData.stats.success_rate}%</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Last 24h:</span>
                            <span className="text-blue-400 ml-2">{syncData.stats.last_24h_syncs}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Avg Time:</span>
                            <span className="text-white ml-2">{syncData.stats.avg_sync_time}s</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recent Sync Records */}
                    <div className="bg-gray-800 rounded p-3">
                      <h5 className="text-yellow-400 font-medium mb-3">🔄 Recent Sync Records ({syncData.records.length})</h5>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {syncData.records.length === 0 ? (
                          <div className="text-gray-400 text-center py-4">No sync records found</div>
                        ) : (
                          syncData.records.map((record, index) => (
                            <div key={record.sync_id || index} className="bg-gray-700 rounded p-3 border-l-4 border-blue-500">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    record.direction === 'outgoing' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                                  }`}>
                                    {record.direction === 'outgoing' ? '↗ OUTGOING' : '↙ INCOMING'}
                                  </span>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    record.status === 'completed' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                                  }`}>
                                    {record.status.toUpperCase()}
                                  </span>
                                  <span className="text-gray-300 text-xs">{record.data_type}</span>
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(record.sync_started_at).toLocaleString()}
                                </div>
                              </div>
                              
                              <div className="text-sm">
                                <div className="text-gray-300">
                                  <span className="text-yellow-400">External ID:</span> {record.external_id}
                                  {record.internal_id && (
                                    <span className="ml-4">
                                      <span className="text-yellow-400">Internal ID:</span> {record.internal_id}
                                    </span>
                                  )}
                                </div>

                                {/* Structured Data Display */}
                                {record.data_payload && (
                                  <div className="mt-2">
                                    <div className="text-yellow-400 text-xs mb-1">📦 Sync Data:</div>
                                    <div className="bg-gray-900 rounded p-3 space-y-2">
                                      {/* Incident/Ticket Information */}
                                      {(record.data_payload.title || record.data_payload.short_description) && (
                                        <div>
                                          <span className="text-blue-300 text-xs font-medium">Title:</span>
                                          <div className="text-white text-sm ml-2">
                                            {record.data_payload.title || record.data_payload.short_description}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {record.data_payload.description && (
                                        <div>
                                          <span className="text-blue-300 text-xs font-medium">Description:</span>
                                          <div className="text-white text-sm ml-2">
                                            {record.data_payload.description}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Status and Priority */}
                                      <div className="flex gap-4">
                                        {record.data_payload.severity && (
                                          <div>
                                            <span className="text-blue-300 text-xs font-medium">Severity:</span>
                                            <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                              record.data_payload.severity === 'high' ? 'bg-red-600 text-white' :
                                              record.data_payload.severity === 'medium' ? 'bg-yellow-600 text-white' :
                                              'bg-green-600 text-white'
                                            }`}>
                                              {record.data_payload.severity.toUpperCase()}
                                            </span>
                                          </div>
                                        )}
                                        
                                        {record.data_payload.priority && (
                                          <div>
                                            <span className="text-blue-300 text-xs font-medium">Priority:</span>
                                            <span className="text-white text-sm ml-2">{record.data_payload.priority}</span>
                                          </div>
                                        )}
                                        
                                        {(record.data_payload.status || record.data_payload.state) && (
                                          <div>
                                            <span className="text-blue-300 text-xs font-medium">Status:</span>
                                            <span className="text-white text-sm ml-2">
                                              {record.data_payload.status || record.data_payload.state}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Assignment */}
                                      {(record.data_payload.assigned_to || record.data_payload.category) && (
                                        <div className="flex gap-4">
                                          {record.data_payload.assigned_to && (
                                            <div>
                                              <span className="text-blue-300 text-xs font-medium">Assigned To:</span>
                                              <span className="text-white text-sm ml-2">{record.data_payload.assigned_to}</span>
                                            </div>
                                          )}
                                          
                                          {record.data_payload.category && (
                                            <div>
                                              <span className="text-blue-300 text-xs font-medium">Category:</span>
                                              <span className="text-white text-sm ml-2">{record.data_payload.category}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Agent Notes - Highlight AI Updates */}
                                      {record.data_payload.agent_notes && (
                                        <div className="bg-blue-900 rounded p-2 border-l-4 border-blue-400">
                                          <span className="text-blue-300 text-xs font-medium">🤖 AI Agent Analysis:</span>
                                          <div className="text-blue-100 text-sm mt-1">
                                            {record.data_payload.agent_notes}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Work Notes */}
                                      {record.data_payload.work_notes && record.data_payload.work_notes.length > 0 && (
                                        <div>
                                          <span className="text-blue-300 text-xs font-medium">💬 Work Notes:</span>
                                          <div className="mt-1 space-y-1">
                                            {record.data_payload.work_notes.map((note, noteIndex) => (
                                              <div key={noteIndex} className="bg-gray-800 rounded p-2 border-l-2 border-gray-600">
                                                <div className="flex justify-between text-xs text-gray-400 mb-1">
                                                  <span>By: {note.author}</span>
                                                  <span>{new Date(note.created_at).toLocaleString()}</span>
                                                </div>
                                                <div className="text-white text-sm">{note.note}</div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* ServiceNow Specific Fields */}
                                      {record.data_payload.number && (
                                        <div>
                                          <span className="text-blue-300 text-xs font-medium">ServiceNow Number:</span>
                                          <span className="text-white text-sm ml-2">{record.data_payload.number}</span>
                                        </div>
                                      )}
                                      
                                      {/* Additional System Fields */}
                                      {(record.data_payload.affected_service || record.data_payload.created_at) && (
                                        <div className="flex gap-4">
                                          {record.data_payload.affected_service && (
                                            <div>
                                              <span className="text-blue-300 text-xs font-medium">Affected Service:</span>
                                              <span className="text-white text-sm ml-2">{record.data_payload.affected_service}</span>
                                            </div>
                                          )}
                                          
                                          {record.data_payload.created_at && (
                                            <div>
                                              <span className="text-blue-300 text-xs font-medium">Created:</span>
                                              <span className="text-white text-sm ml-2">
                                                {new Date(record.data_payload.created_at).toLocaleString()}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Raw JSON Toggle */}
                                    {showRawData && (
                                      <div className="mt-3 pt-3 border-t border-gray-700">
                                        <div className="text-yellow-400 text-xs mb-1">🔧 Raw JSON Data:</div>
                                        <div className="bg-black rounded p-2 overflow-x-auto max-h-40">
                                          <pre className="text-xs text-green-400 whitespace-pre-wrap">
                                            {JSON.stringify(record.data_payload, null, 2)}
                                          </pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Sync Notes - Show Agent Updates */}
                                {record.sync_notes && record.sync_notes.length > 0 && (
                                  <div className="mt-2">
                                    <div className="text-yellow-400 text-xs mb-1">🤖 Sync Notes & Agent Updates:</div>
                                    <div className="space-y-1">
                                      {record.sync_notes.map((note, noteIndex) => (
                                        <div key={noteIndex} className="text-xs text-blue-300 bg-gray-900 rounded px-2 py-1">
                                          • {note}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">ServiceNow Mock Data Flow</h4>
            <p className="text-sm text-blue-800">✅ Bidirectional sync working correctly</p>
            <p className="text-sm text-blue-800">✅ Incident numbers: INC0000001, INC0000002</p>
            <p className="text-sm text-blue-800">✅ Agent updates syncing to ServiceNow</p>
          </div>
        </div>
      </div>

      {/* Create Integration Modal */}
      {showCreateModal && (
        <CreateIntegrationModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateIntegration}
          creating={creating}
        />
      )}
    </div>
  );
};

// Create Integration Modal Component
const CreateIntegrationModal = ({ onClose, onCreate, creating }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'MONITORING',
    provider: '',
    config: {},
    credentials: {},
    endpoints: {}
  });

  const integrationTypes = [
    'MONITORING', 'ALERTING', 'LOGGING', 'METRICS', 'APM', 
    'CLOUD_PROVIDER', 'NOTIFICATION', 'TICKETING', 'CI_CD'
  ];

  const providersByType = {
    MONITORING: ['Datadog', 'New Relic', 'Prometheus', 'Grafana', 'SAP Focused Run'],
    ALERTING: ['PagerDuty', 'Opsgenie', 'AlertManager'],
    NOTIFICATION: ['Slack', 'Microsoft Teams', 'Email', 'SMS'],
    TICKETING: ['ServiceNow', 'Jira', 'Zendesk', 'GitHub Issues'],
    CLOUD_PROVIDER: ['AWS', 'Azure', 'Google Cloud', 'DigitalOcean'],
    LOGGING: ['Splunk', 'ELK Stack', 'Fluentd', 'Logstash'],
    METRICS: ['Prometheus', 'InfluxDB', 'CloudWatch', 'Azure Monitor'],
    APM: ['New Relic', 'Datadog APM', 'AppDynamics', 'Dynatrace'],
    CI_CD: ['Jenkins', 'GitHub Actions', 'GitLab CI', 'Azure DevOps']
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.provider) {
      alert('Please fill in all required fields');
      return;
    }
    onCreate(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Create New Integration</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={creating}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Production Monitoring"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={creating}
            />
          </div>

          {/* Type Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => {
                handleInputChange('type', e.target.value);
                handleInputChange('provider', ''); // Reset provider when type changes
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={creating}
            >
              {integrationTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Provider Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Provider *
            </label>
            <select
              value={formData.provider}
              onChange={(e) => handleInputChange('provider', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={creating}
            >
              <option value="">Select a provider</option>
              {(providersByType[formData.type] || []).map(provider => (
                <option key={provider} value={provider}>{provider}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={creating}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Integration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationManagement;